import { useCallback, useEffect, useState, useRef } from "react";

import { AgGridReact } from "ag-grid-react";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import { styled } from '@mui/material/styles';
import Box from "@mui/material/Box"
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ResetIcon from "@mui/icons-material/RotateLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowForward";
import TextField from "@mui/material/TextField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import Checkbox from "@mui/material/Checkbox";
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from "@mui/material/CardContent";
import { blue, blueGrey, green, grey, lightBlue, red } from "@mui/material/colors";

import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";
import { format } from "date-fns";

import cycleCalendarService from "@plannel/services/data/cycle-calendar-service";
import calendarGroupService from "@plannel/services/data/calendar-group-service";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, EditButton, RemoveButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import { DEFAULT_DATE_FORMAT } from '@plannel/utils/date-util';
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import DataState from "@plannel/components/aggrid/DataState"
import { createRoundToggleButton } from "@plannel/components/ui/RoundToggleButton";
import { FS } from "@plannel/utils/font-util";
//import NumericEditor from "@plannel/components/aggrid/NumericEditor";

const { cycleUoms } = CodeUtil;

// 원형 토글(요일/주/월/일자) — ui 팩토리로 통일. 기존 in-render styled 중복/재생성 제거.
const DowToggle = createRoundToggleButton(grey, { hoverShade: 200, hoverText: true });
const DowToggleSun = createRoundToggleButton(red);
const DowToggleSat = createRoundToggleButton(blue);
const WeekToggle = createRoundToggleButton(green, { diameter: 55 });
const MonthToggle = createRoundToggleButton(green, { diameter: 45 });
const DateToggle = createRoundToggleButton(lightBlue);

/*
 * 기간 DatePicker 팝업의 뷰 높이 바닥값 — 연/월/일 뷰 전환 시 팝업 높이가 변하면 Popper 가
 * 위↔아래로 flip 되고, 그 클릭이 팝업 밖 클릭으로 인식돼 달력이(환경에 따라 이 모달까지) 닫힌다.
 * 월 → 일 전환에서 발생하고, 필드 아래 남은 공간이 "월 뷰는 들어가지만 일 뷰는 안 들어가는"
 * 경계에 걸린 환경에서만 재현되므로 해상도·브라우저 확대비율·창 크기에 따라 갈린다.
 *
 * App.scss 의 `.MuiDateCalendar-root { height: auto !important }` 가 MUI 의 고정 높이
 * (VIEW_HEIGHT = 334)를 풀어놔서, 자기 높이 규칙이 없는 월 뷰만 100px 이상 짧아지는 것.
 * min-height 는 height 와 다른 속성이라 그 !important 와 충돌하지 않고 바닥값만 되돌린다.
 */
const PICKER_VIEW_SX = { "& .MuiDateCalendar-root": { minHeight: 334 } };

const CalendarModal = ({ modalOpen, setModalOpen, selectedCycle, editedCell, activeControl = false, showDetail = false, readOnly = false, setSnackInfo, retrieve }) => {
  const { t } = useTranslation();
  
  const INIT_DATE = format(new ZDate(), DEFAULT_DATE_FORMAT);
  const INIT_CYCLE = {
    CYCLE: { cycleName: "", cycleDesc: "", cycleValue: 1, cycleUom: "S", calendarGroup: null },
    PERIOD: { startDate: INIT_DATE, endDate: INIT_DATE }
  };
  const EMPTY_LIST = [];

  const [newCycle, setNewCycle] = useState(INIT_CYCLE.CYCLE);
  const [period, setPeriod] = useState(INIT_CYCLE.PERIOD);

  const [calendarGrps, setCalendarGrps] = useState(EMPTY_LIST);
  const [days, setDays] = useState(EMPTY_LIST);
  const [dates, setDates] = useState(EMPTY_LIST);
  const [weeks, setWeeks] = useState(EMPTY_LIST);
  const [months, setMonths] = useState(EMPTY_LIST);

  const [onDate, setOnDate] = useState(false);
  const [noEndDate, setNoEndDate] = useState(true);

  const [currentCycle, setCurrentCycle] = useState({});

  const [active, setActive] = useState(true);
  const [isGridMode, setIsGridMode] = useState(true);
  const { snackBarMessageColor } = ColorUtil;
  const gridRef = useRef();
  //const [demandRatios, setDemandRatios] = useState([]);

  const clearCycleCalendar = () => {
    setNewCycle(INIT_CYCLE.CYCLE);
    setPeriod(INIT_CYCLE.PERIOD);

    setDays(EMPTY_LIST);
    setDates(EMPTY_LIST);
    setWeeks(EMPTY_LIST);
    setMonths(EMPTY_LIST);

    setOnDate(false);
    setNoEndDate(true);
    //setDemandRatios([]);
  }

  const initDow = initValue => {
    const { dowSunFlg, dowMonFlg, dowTueFlg, dowWedFlg, dowThuFlg, dowFriFlg, dowSatFlg } = initValue;
    const dows = [dowSunFlg, dowMonFlg, dowTueFlg, dowWedFlg, dowThuFlg, dowFriFlg, dowSatFlg];
    const selectedDows = [];
    dows.forEach((dow, index) => {
      if (dow) {
        selectedDows.push(index);
      }
    });
    setDays(selectedDows);
  }

  const initWom = initValue => {
    const { wom1wFlg, wom2wFlg, wom3wFlg, wom4wFlg, wom5wFlg } = initValue;
    const woms = [wom1wFlg, wom2wFlg, wom3wFlg, wom4wFlg, wom5wFlg];
    const selectedWoms = [];
    woms.forEach((wom, index) => {
      if (wom) {
        selectedWoms.push(index);
      }
    });
    setWeeks(selectedWoms);
  }

  const initCycleCalendar = initValue => {
    if (!isEmpty(initValue)) {
      const { name, descTxt, calendarGroup, intervalVal, cycleCd, startDate, endDate, domObj } = initValue;
      setNewCycle({ cycleName: name, cycleDesc: descTxt, calendarGroup, cycleValue: intervalVal, cycleUom: cycleCd });
      setPeriod({ startDate, endDate });

      if (endDate) {
        setNoEndDate(false);
      }

      if (activeControl) {
        setActive(initValue.activeFlg);
      }

      if (isEqual(cycleCd, "W")) {
        initDow(initValue);
      } else if (isEqual(cycleCd, "M")) {
        if (domObj?.onDates) {
          setDates(domObj.onDates);
          setOnDate(true);
        } else {
          initDow(initValue);
          initWom(initValue);
        }
      } else if (isEqual(cycleCd, "Y")) {
        if (domObj?.onMonth) {
          setMonths([domObj.onMonth]);
        }
        if (domObj?.onDate) {
          setDates([domObj.onDate]);
        }
      }
    }
  }
  
  const retrieveCalendarGroup = () => {
    calendarGroupService.getAll()
      .then(response => {
        const { results } = response.data;
        setCalendarGrps(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const saveCycleCalendar = (cycleCalendarObj) => {
    cycleCalendarService.upsert(cycleCalendarObj)
      .then((response) => {
        const { cycleCalendar } = response.data;

        if (!isEmpty(cycleCalendar)) {
          if (editedCell) {
            editedCell.node.setDataValue(editedCell.column, cycleCalendar);
          }
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          setModalOpen(false);
          clearCycleCalendar();
          if (retrieve) {
            retrieve();
          }
        }
      })
      .catch(e => {
        const { message } = e.response?.data;
        if (message) {
          setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
        debug(e);
      });
  }

  const isValidInputs = () => {
    if (isEmpty(newCycle.cycleName)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoName") });
      return false;
    }

    const { cycleUom, cycleValue } = newCycle;

    if (!isEqual(cycleUom, "S") && (isEmpty(cycleValue.toString()) || cycleValue <= 0)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoCycle") });
      return false;
    }

    if (cycleUom === "W") {
      if (isEmpty(days)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoDay") });
        return false;
      }
    }

    if (cycleUom === "M") {
      if (onDate) {
        if (isEmpty(dates)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoDate") });
          return false;
        }
      } else {
        if (isEmpty(weeks) || isEmpty(days)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoWeekDay") });
          return false;
        }
      }
    }

    if (cycleUom === "Y") {
      if (typeof months !== "number" || typeof dates !== "number") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoMonthDate") });
        return false;
      }
    }

    if (!noEndDate) {
      if (period.endDate < period.startDate) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SetEndDate") });
        return false;
      }
    }

    return true;
  }

  const handleClose = action => {
    if (action === "set") {
      if (isValidInputs()) {
        const startDate = period.startDate;
        const endDate = noEndDate ? null : period.endDate;

        gridRef?.current?.api?.stopEditing(false);

        const allStateData = [];

        gridRef?.current?.api?.forEachNode((node) => {
          allStateData.push(node.data);
        });

        const cycleCalendarObj = {
          ...currentCycle,
          name: newCycle.cycleName,
          calendarGroup: newCycle.calendarGroup,
          startDate,
          endDate,
          cycleCd: newCycle.cycleUom,
          intervalVal: Number(newCycle.cycleValue),
          descTxt: newCycle.cycleDesc,
          domObj: {},
          activeFlg: activeControl ? active : true
        };

        if (newCycle.cycleUom === "S") {
          cycleCalendarObj.domObj = null;
          cycleCalendarObj.intervalVal = 1;
        } else if (newCycle.cycleUom === "W") {
          cycleCalendarObj.dowSunFlg = days.includes(0);
          cycleCalendarObj.dowMonFlg = days.includes(1);
          cycleCalendarObj.dowTueFlg = days.includes(2);
          cycleCalendarObj.dowWedFlg = days.includes(3);
          cycleCalendarObj.dowThuFlg = days.includes(4);
          cycleCalendarObj.dowFriFlg = days.includes(5);
          cycleCalendarObj.dowSatFlg = days.includes(6);
        } else if (newCycle.cycleUom === "M") {
          if (onDate) {
            cycleCalendarObj.domObj.onDates = dates;
          } else {
            cycleCalendarObj.dowSunFlg = days.includes(0);
            cycleCalendarObj.dowMonFlg = days.includes(1);
            cycleCalendarObj.dowTueFlg = days.includes(2);
            cycleCalendarObj.dowWedFlg = days.includes(3);
            cycleCalendarObj.dowThuFlg = days.includes(4);
            cycleCalendarObj.dowFriFlg = days.includes(5);
            cycleCalendarObj.dowSatFlg = days.includes(6);
            cycleCalendarObj.wom1wFlg = weeks.includes(0);
            cycleCalendarObj.wom2wFlg = weeks.includes(1);
            cycleCalendarObj.wom3wFlg = weeks.includes(2);
            cycleCalendarObj.wom4wFlg = weeks.includes(3);
            cycleCalendarObj.wom5wFlg = weeks.includes(4);
            cycleCalendarObj.wom1wRatio = allStateData[0]?.wom1wRatio;
            cycleCalendarObj.wom2wRatio = allStateData[0]?.wom2wRatio;
            cycleCalendarObj.wom3wRatio = allStateData[0]?.wom3wRatio;
            cycleCalendarObj.wom4wRatio = allStateData[0]?.wom4wRatio;
            cycleCalendarObj.wom5wRatio = allStateData[0]?.wom5wRatio;
          }
        } else if (newCycle.cycleUom === "Y") {
          cycleCalendarObj.domObj.onMonth = months;
          cycleCalendarObj.domObj.onDate = dates;
        }
        
        saveCycleCalendar(cycleCalendarObj);
      }
    } else if (action === "cancel") {
      if (showDetail) {
        isGridMode ? setModalOpen(false) : setIsGridMode(true);
      } else {
        setModalOpen(false);
      }
      clearCycleCalendar();
    }
  };

  const handleTfChange = useCallback(e => {
    const { name, value } = e.target;

    setNewCycle(originData => ({
      ...originData,
      [name]: value
    }));

  }, []);

  const onChangePeriod = (newValue, identifier) => {
    setPeriod({ ...period, [identifier]: newValue })
  }

  const onChangeCycle = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === "cycleValue") {
      setNewCycle({ ...newCycle, cycleValue: value });
    }

    if (name === "cycleUom") {
      setNewCycle({ ...newCycle, cycleUom: value, cycleValue: 1 });
      setDays([]);
      setDates([]);
      setWeeks([]);
      setMonths([]);
    }
  };

  const onChangeWithWeek = () => {
    setOnDate(!onDate);
    setDays([]);
    setDates([]);
    setWeeks([]);
    setMonths([]);
  }

  const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
      marginRight: 16,
      marginTop: 4,
      marginBottom: 4,
      padding: theme.spacing(0, 1),
      border: "1px solid !important",
      marginLeft: "-1px !important",
      "&:not(:first-of-type)": {
        border: "1px solid",
        borderRadius: "50%"
      },
      "&:first-of-type": {
        border: "1px solid",
        borderRadius: "50%"
      }
    }
  }));

  const ToggleDays = () => {
    const DAYS = [
      { value: 0, label: t("sun") },
      { value: 1, label: t("mon") },
      { value: 2, label: t("tue") },
      { value: 3, label: t("wed") },
      { value: 4, label: t("thu") },
      { value: 5, label: t("fri") },
      { value: 6, label: t("sat") }
    ];

    const StyledToggle = DowToggle;

    const StyledToggleSun = DowToggleSun;

    const StyledToggleSat = DowToggleSat;

    return (
      <AppCard sx={{ padding: 1, paddingLeft: 4 }} variant="outlined">
        <StyledToggleButtonGroup
          sx={{ mt: 3 }}
          size="small"
          arial-label="Days of the week"
          value={days}
          disabled={readOnly}
          onChange={(_, value) => setDays(value)}
        >
          {DAYS.map(day => {
            if (day.value === 0) {
              return <StyledToggleSun key={day.value} value={day.value} aria-label={day.label}>{day.label}</StyledToggleSun>
            } else if (day.value === 6) {
              return <StyledToggleSat key={day.value} value={day.value} aria-label={day.label}>{day.label}</StyledToggleSat>
            }

            return <StyledToggle key={day.value} value={day.value} aria-label={day.label}>{day.label}</StyledToggle>
          })}
        </StyledToggleButtonGroup>
      </AppCard>
    );
  };

  const ToggleWeeks = () => {
    const WEEKS = [
      { value: 0, label: t("first") },
      { value: 1, label: t("second") },
      { value: 2, label: t("third") },
      { value: 3, label: t("fourth") },
      { value: 4, label: t("fifth") }
    ];

    const StyledToggle = WeekToggle;

    return (
      <AppCard sx={{ padding: 1, paddingLeft: 4 }} variant="outlined">
        <StyledToggleButtonGroup
          sx={{ mt: 3 }}
          size="small"
          arial-label="Weeks of the month"
          value={weeks}
          disabled={readOnly}
          onChange={(_event, value) => setWeeks(value)}
        >
          {WEEKS.map(week => {
            return <StyledToggle key={week.value} value={week.value} aria-label={week.label}>{week.label}</StyledToggle>
          })}
        </StyledToggleButtonGroup>
      </AppCard>
    );
  };

  const ToggleMonths = () => {
    const MONTHS = [
      { value: 0, label: t("jan") },
      { value: 1, label: t("feb") },
      { value: 2, label: t("mar") },
      { value: 3, label: t("apr") },
      { value: 4, label: t("may") },
      { value: 5, label: t("jun") },
      { value: 6, label: t("jul") },
      { value: 7, label: t("aug") },
      { value: 8, label: t("sep") },
      { value: 9, label: t("oct") },
      { value: 10, label: t("nov") },
      { value: 11, label: t("dec") }
    ];

    const StyledToggle = MonthToggle;

    return (
      <AppCard sx={{ padding: 1, textAlign: "center" }} variant="outlined">
        <StyledToggleButtonGroup
          sx={{ mt: 3 }}
          size="small"
          exclusive
          arial-label="Weeks of the month"
          value={months}
          disabled={readOnly}
          onChange={(_event, value) => setMonths(value)}
        >
          {MONTHS.map(month => {
            if (month.value < 6) {
              return <StyledToggle key={month.value} value={month.value} aria-label={month.label}>{month.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          size="small"
          exclusive
          arial-label="Weeks of the month"
          value={months}
          onChange={(_event, value) => setMonths(value)}
        >
          {MONTHS.map(month => {
            if (month.value >= 6) {
              return <StyledToggle key={month.value} value={month.value} aria-label={month.label}>{month.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
      </AppCard>
    );
  };

  const ToggleDates = () => {
    const DATES = ((maxDates) => {
      const dateItems = [];

      for (let i = 1; i <= maxDates; i++) {
        dateItems.push({ value: i, label: i })
      }

      return dateItems;
    })(31);

    const StyledToggle = DateToggle;

    return (
      <AppCard sx={{ padding: 1, paddingLeft: 2 }} variant="outlined">
        <StyledToggleButtonGroup
          sx={{ mt: 3 }}
          size="small"
          exclusive={newCycle.cycleUom === "Y"}
          arial-label="Weeks of the month"
          value={dates}
          disabled={readOnly}
          onChange={(_event, value) => setDates(value)}
        >
          {DATES.map(date => {
            if (date.value < 8) {
              return <StyledToggle key={date.value} value={date.value} aria-label={date.label}>{date.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          size="small"
          exclusive={newCycle.cycleUom === "Y"}
          arial-label="Weeks of the month"
          value={dates}
          disabled={readOnly}
          onChange={(_event, value) => setDates(value)}
        >
          {DATES.map(date => {
            if (date.value >= 8 && date.value < 15) {
              return <StyledToggle key={date.value} value={date.value} aria-label={date.label}>{date.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          size="small"
          exclusive={newCycle.cycleUom === "Y"}
          arial-label="Weeks of the month"
          value={dates}
          disabled={readOnly}
          onChange={(_event, value) => setDates(value)}
        >
          {DATES.map(date => {
            if (date.value >= 15 && date.value < 22) {
              return <StyledToggle key={date.value} value={date.value} aria-label={date.label}>{date.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          size="small"
          exclusive={newCycle.cycleUom === "Y"}
          arial-label="Weeks of the month"
          value={dates}
          disabled={readOnly}
          onChange={(_event, value) => setDates(value)}
        >
          {DATES.map(date => {
            if (date.value >= 22 && date.value < 29) {
              return <StyledToggle key={date.value} value={date.value} aria-label={date.label}>{date.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          size="small"
          exclusive={newCycle.cycleUom === "Y"}
          arial-label="Weeks of the month"
          value={dates}
          disabled={readOnly}
          onChange={(_event, value) => setDates(value)}
        >
          {DATES.map(date => {
            if (date.value >= 29) {
              return <StyledToggle key={date.value} value={date.value} aria-label={date.label}>{date.label}</StyledToggle>
            }

            return null;
          })}
        </StyledToggleButtonGroup>
      </AppCard>
    );
  };

  const NoEndDateStyledToggle = styled(ToggleButton)(() => ({
    '&.MuiToggleButton-root': {
      color: blueGrey[700],
      minWidth: 40,
      maxWidth: 40,
      height: 40,
      textTransform: "unset",
      fontSize: FS.SUB,
      border: "1px solid",
      borderRadius: "15%",
      "&.Mui-selected": {
        color: "white",
        background: green[700]
      },
      "&:hover": {
        borderColor: green[100],
        background: green[100]
      },
      "&:hover$selected": {
        borderColor: blueGrey[100],
        background: blueGrey[100]
      }
    }
  }));

  /*
   * 주기 캘린더 목록(그리드 모드) 상태 — 셸(BaseConfigModal)의 액션 버튼이 이 값들을 만져야 해서
   * 부모 스코프에 둔다. 예전엔 CycleCalendarGrid 를 렌더 함수 안에서 "컴포넌트"로 정의했는데,
   * 그러면 부모가 리렌더될 때마다 React 가 새 타입으로 보고 통째로 재마운트한다 —
   * 그 상태로 gridApi 만 부모로 올리면 재마운트↔onGridReady 가 서로를 불러 무한 루프가 된다.
   * 그래서 컴포넌트가 아니라 **엘리먼트**(cycleCalendarGrid)로 바꿨다.
   */
  const [gridApi, setGridApi] = useState(null);
  const [cycleCalendars, setCycleCalendars] = useState([]);
  const [snackGridInfo, setGridSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const disabledColor = { background: grey[300], opacity: 0.5 };

  const setWomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "M") && !data.domObj?.onDates) {
      return {};
    }

    return disabledColor;
  }

  const setDomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "W") || (isEqual(data.cycleCd, "M") && !data.domObj?.onDates)) {
      return {};
    }

    return disabledColor;
  }

  const valueFormatter = (params) => {
    return params.value?.name ? params.value.name : "";
  }

  const columnDefs = [
    {
      headerName: "calendarName", field: "name", type: ["nonEditableColumn"], width: 200
    },
    {
      headerName: "repeatEvery", groupId: "repeatEvery",
      children: [
        {
          headerName: "", field: "intervalVal", type: ["numberColumn", "nonEditableColumn"], width: 90,
          valueFormatter: params => isEqual(params.data?.cycleCd, "S") ? "" : params.value,
          cellStyle: params => {
            const { data } = params;
            if (isEqual(data.cycleCd, "S")) {
              return disabledColor;
            }
            return {};
          }
        },
        {
          headerName: "", field: "cycleCd", type: ["nonEditableColumn"], width: 90,
          valueFormatter: params => {
            const { value } = params;
            const uomIndex = cycleUoms.findIndex(uom => isEqual(uom.value, value));
            if (uomIndex > -1) {
              const cycleUom = cycleUoms[uomIndex];
              return t(cycleUom.text);
            }
  
            return value;
          }
        }
      ]
    },
    {
      headerName: "period", groupId: "period",
      children: [
        { headerName: "startDate", field: "startDate", colId: "startDate", type: ["nonEditableColumn"], width: 120 },
        { headerName: "endDate", field: "endDate", colId: "endDate", type: ["nonEditableColumn"], width: 120 }
      ]
    },
    { headerName: "calendarGrpName", field: "calendarGroup", type: ["nonEditableColumn"], width: 150, valueFormatter },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 200 },
    // Month of Year
    {
      headerName: "monthOfYear", field: "domObj", type: ["nonEditableColumn"], width: 130,
      valueFormatter: params => {
        const { value } = params;
        if (value?.onMonth) {
          return value.onMonth + 1;
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y")) {
          return {};
        }
        return disabledColor;
      }
    },
    // Day of Month
    {
      headerName: "dateOfMonth", field: "domObj", type: ["nonEditableColumn"], width: 120, 
      valueFormatter: params => {
        const { value } = params;

        if (value?.onDate) {
          return value.onDate;
        } else if (value?.onDates) {
          return value.onDates.join(", ");
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y") || (isEqual(data.cycleCd, "M") && data.domObj?.onDates)) {
          return {};
        }
        return disabledColor;
      }
    },
    // Week of Month
    {
      headerName: "weekOfMonth", groupId: "wom",
      children: [
        { headerName: "first", field: "wom1wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "second", field: "wom2wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "third", field: "wom3wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fourth", field: "wom4wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fifth", field: "wom5wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
      ]
    },
    // {
    //   headerName: "disaggRatio", groupId: "wom",
    //   children: [
    //     { headerName: "first", field: "wom1wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "second", field: "wom2wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "third", field: "wom3wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "fourth", field: "wom4wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "fifth", field: "wom5wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor }
    //   ]
    // },
    // Day of Week
    {
      headerName: "dayOfWeek", groupId: "dow",
      children: [
        { headerName: "mon", field: "dowMonFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "tue", field: "dowTueFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "wed", field: "dowWedFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "thu", field: "dowThuFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "fri", field: "dowFriFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sat", field: "dowSatFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sun", field: "dowSunFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
      ]
    }
  ];

  const retrieveCycleCalendar = () => {
    cycleCalendarService.getAll({ searchFilters: { activeFlg: true } })
      .then(response => {
        const { results } = response.data;
        setCycleCalendars(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const deleteCycleCalendar = () => {
    const cycleCalendarIds = [];
    gridApi.getSelectedRows().forEach(data => {
      cycleCalendarIds.push(data.id);
    });

    if (cycleCalendarIds.length > 0) {
      cycleCalendarService.remove(cycleCalendarIds)
        .then(() => {
          setGridSnackInfo({ open: true, severity: "success", content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setGridSnackInfo({ open: true, severity: "error", content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveCycleCalendar();
        });
    } else {
      setGridSnackInfo({ open: true, severity: "info", content: t("MSG_NoSelectedData") });
    }
  }

  const addCycleCalendar = () => {
    clearCycleCalendar();
    setIsGridMode(false);
  }

  const selectCycleCalendar = () => {
    const selectedRows = gridApi.getSelectedRows();
    const value = isEmpty(selectedRows) ? null : selectedRows[0];
    
    if (editedCell) {
      editedCell.node.setDataValue(editedCell.column, value);
    }
    setModalOpen(false);
    setIsGridMode(true);
    clearCycleCalendar();
  }

  const resetCycleCalendar = () => {
    if (editedCell) {
      editedCell.node.setDataValue(editedCell.column, null);
    }
    setModalOpen(false);
    setIsGridMode(true);
    clearCycleCalendar();
  }

  const editCycleCalendar = () => {
    const selectedRows = gridApi.getSelectedRows();
    if (isEmpty(selectedRows)) {
      setGridSnackInfo({ open: true, severity: "info", content: t("MSG_NoSelectedRow") });
    } else {
      setCurrentCycle(selectedRows[0]);
      initCycleCalendar(selectedRows[0]);
      //setDemandRatios([{...selectedRows[0]}]);
      setIsGridMode(false);
    }
  }

  const onClickButton = action => {
    if (action === "set") {
      selectCycleCalendar();
    } else if (action === "cancel") {
      setModalOpen(false);
      setIsGridMode(true);
      clearCycleCalendar();
    } else if (action === "reset") {
      resetCycleCalendar();
    }
  }

  const handleDialog = action => {
    if (action === "DELETE") {
      deleteCycleCalendar();
    }
  }

  const onFirstDataRendered = params => {
    if (params.api && editedCell.value) {
      params.api.forEachNode(node => {
        const { name } = node.data;

        if (isEqual(editedCell.value, name)) {
          node.setSelected(true);
        }
      });
      
      GridUtils.focusOnSelectedRow(gridApi);
    }
  }

  useEffect(() => {
    if (gridApi) {
      retrieveCycleCalendar();
    }

    return () => {
      setCycleCalendars([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi]);

  // 그리드 모드 본문 — 제목/강조선/액션은 셸이 그린다
  const cycleCalendarGrid = (
    <>
      <Box sx={{ ml: "auto", mr: 1.5, textAlign: "right" }}>
        <AddButton onClick={addCycleCalendar} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <EditButton onClick={editCycleCalendar} />
      </Box>
      <Box className="ag-theme-balham" sx={{ height: "40vh" }} mt={1} >
        <AgGridReact
          rowData={cycleCalendars}
          columnDefs={GridUtils.getColumnDefs(columnDefs)}
          {...DefaultGridSetting({})}
          rowSelection={{ mode: 'singleRow', enableClickSelection: true }}
          onGridReady={params => setGridApi(params.api)}
          onFirstDataRendered={onFirstDataRendered}
          onCellDoubleClicked={selectCycleCalendar}
        />
      </Box>
    </>
  );

  useEffect(() => {
    if (modalOpen) {
      retrieveCalendarGroup();
      //setDemandRatios([]);
      
      if (selectedCycle) {
        DataState.initialize(gridRef.current?.api);
        setCurrentCycle(selectedCycle);
        initCycleCalendar(selectedCycle);
        //setDemandRatios([{...selectedCycle}]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // const columnDefs = [
  //   { headerName: "first", field: "wom1wRatio", type: ["numberColumn", "rightAligned"], cellEditor: NumericEditor, width: 74, editable: weeks.includes(0) /*cellStyle: setWomDisabledColor*/ },
  //   { headerName: "second", field: "wom2wRatio", type: ["numberColumn", "rightAligned"], cellEditor: NumericEditor, width: 74, editable: weeks.includes(1) /*cellStyle: setWomDisabledColor*/ },
  //   { headerName: "third", field: "wom3wRatio", type: ["numberColumn", "rightAligned"], cellEditor: NumericEditor, width: 74, editable: weeks.includes(2) /*cellStyle: setWomDisabledColor*/ },
  //   { headerName: "fourth", field: "wom4wRatio", type: ["numberColumn", "rightAligned"], cellEditor: NumericEditor, width: 74, editable: weeks.includes(3) /*cellStyle: setWomDisabledColor*/ },
  //   { headerName: "fifth", field: "wom5wRatio", type: ["numberColumn", "rightAligned"], cellEditor: NumericEditor, width: 74, editable: weeks.includes(4) /*cellStyle: setWomDisabledColor*/ },
  // ]

  const isCycleValCompVisible = isEqual(newCycle.cycleUom, "S") ? "none" : "";
  const isDayPickCompVisible = isEqual(newCycle.cycleUom, "W") || (isEqual(newCycle.cycleUom, "M") && !onDate) ? "" : "none";
  const isDatePickCompVisible = isEqual(newCycle.cycleUom, "Y") || (isEqual(newCycle.cycleUom, "M") && onDate) ? "" : "none";
  const isWeekPickCompVisible = isEqual(newCycle.cycleUom, "M") && !onDate ? "" : "none";
  const isOnDateCompVisible = isEqual(newCycle.cycleUom, "M") ? "" : "none";
  const isMonthPickCompVisible = isEqual(newCycle.cycleUom, "Y") ? "" : "none";

  // const onGridReady = () => {
  //   retrieveCalendarGroup();
  // }

  useEffect(() => {
    if (gridRef && DataState) {
      const allData = [];
      gridRef?.current?.api?.forEachNode((node) => {
        allData.push(node.data);
      });

      if (allData.length > 0) {
        for (let i = 0; i < 5; i++) { 
          if (!weeks.includes(i)) {
            const field = `wom${i + 1}wRatio`;
            allData[0][field] = 0;
          }
        }

        const updatedRow = gridRef?.current?.api?.applyTransaction({ update: allData }).update;  
  
        gridRef?.current?.api?.redrawRows({  update: updatedRow });
      }
    }
  }, [weeks]); 

  /*
   * 한 셸에 두 모드가 들어간다.
   *  · 그리드 모드(showDetail): 목록에서 골라 셀에 넣기만 한다 → 저장이 아니므로 confirm + DoneAll, Reset 은 부가 액션
   *  · 폼 모드            : saveCycleCalendar() 로 실제 저장 → save + SaveIcon. readOnly 면 저장 버튼 자체가 없다
   */
  const isGridBranch = showDetail && isGridMode;

  return (
    <>
    <BaseConfigModal
      open={modalOpen}
      onClose={() => (isGridBranch ? onClickButton("cancel") : handleClose("cancel"))}
      title={isGridBranch ? t("setCalendar") : (readOnly ? t("calendar") : t("setCalendar"))}
      onSave={isGridBranch ? () => onClickButton("set") : (readOnly ? undefined : () => handleClose("set"))}
      saveLabel={isGridBranch ? t("confirm") : undefined}
      saveIcon={isGridBranch ? <DoneAllIcon /> : undefined}
      extraActions={isGridBranch
        ? <Button size="small" startIcon={<ResetIcon />} onClick={() => onClickButton("reset")}>{t("reset")}</Button>
        : null}
      width={isGridBranch ? 1432 : 422}
    >
      {isGridBranch ? cycleCalendarGrid : (
          <>
            <Box sx={{ display: "flex" }}>
              <TextField
                id="cycleNameTf"
                name="cycleName"
                label={t("name")}
                sx={{ width: "100%" }}
                value={newCycle.cycleName}
                onChange={handleTfChange}
                size="small"
                variant="outlined"
                InputProps={{ readOnly }}
              />
            </Box>
            <Box sx={{ display: "flex", mt: 1.5 }}>
              <TextField
                id="cycleDescTf"
                name="cycleDesc"
                label={t("descTxt")}
                sx={{ width: "100%" }}
                value={newCycle?.cycleDesc ? newCycle.cycleDesc : ""}
                onChange={handleTfChange}
                size="small"
                variant="outlined"
                InputProps={{ readOnly }}
              />
            </Box>
            <Box sx={{ display: "flex", mt: 1.5 }}>
              <Autocomplete
                options={calendarGrps}
                getOptionLabel={option => option.name}
                value={newCycle?.calendarGroup ?? null}
                onChange={(_, newValue) => setNewCycle({ ...newCycle, calendarGroup: newValue})}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                size="small"
                sx={{ width: "100%" }}
                readOnly={readOnly}
                renderInput={(params) => (
                  <TextField {...params} label={t("calendarGrpName")} />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", mt: 1.5 }}>
              <FormControl size="small" key={"repeatEvery"} sx={{ width: "100%" }}>
                <InputLabel>{t("repeatEvery")}</InputLabel>
                <AppCard variant="outlined">
                  <CardContent sx={{ mt: 3, pb: "12px !important", display: "flex" }}>
                    <TextField
                      id="cycleValueTf"
                      name="cycleValue"
                      sx={{ width: "60px", display: isCycleValCompVisible }}
                      value={newCycle.cycleValue ? newCycle.cycleValue : ""}
                      onChange={onChangeCycle}
                      size="small"
                      type="number"
                      InputProps={{ inputProps: { min: 1 }, readOnly }}
                      variant="outlined"
                    />
                    <FormControl size="small" sx={{ ml: newCycle.cycleUom === "S" ? 0 : 1 }} key={"cycleUom"}>
                      <Select
                        name={"cycleUom"}
                        size="small"
                        sx={{ width: "120px" }}
                        value={newCycle.cycleUom ? newCycle.cycleUom : ""}
                        onChange={onChangeCycle}
                        inputProps={{ readOnly }}>
                        {cycleUoms ? cycleUoms.map((cycleUom) => {
                          return <MenuItem value={cycleUom.value} key={cycleUom.value}>{t(cycleUom.text)}</MenuItem>
                        }) : ""}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormControlLabel
                        control={<Checkbox color="primary" checked={onDate} onChange={onChangeWithWeek} disabled={readOnly} />}
                        label={t("setDate")}
                        sx={{ ml: 0.5, height: "40px", display: isOnDateCompVisible }}
                      />
                    </FormControl>
                  </CardContent>
                </AppCard>
              </FormControl>
            </Box>
            <Box sx={{ mt: 1.5, display: isWeekPickCompVisible }}>
              <FormControl size="small" key={"wom"} sx={{ width: "100%" }}>
                <InputLabel>{t("weekOfMonth")}</InputLabel>
                <ToggleWeeks />
              </FormControl>
              {/* <Box className="ag-theme-balham modal-grid-area" sx={{ height: 97 }} mt={1} >
                <AgGridReact
                  ref={gridRef}
                  rowData={demandRatios}
                  columnDefs={GridUtils.getColumnDefs(columnDefs)}
                  {...DefaultGridSetting({})}
                  onGridReady={onGridReady}
                />
              </Box> */}
            </Box>
            <Box sx={{ mt: 1.5, display: isDayPickCompVisible }}>
              <FormControl size="small" key={"dow"} sx={{ width: "100%" }}>
                <InputLabel>{t("dayOfWeek")}</InputLabel>
                <ToggleDays />
              </FormControl>
            </Box>
            <Box sx={{ mt: 1.5, display: isMonthPickCompVisible }}>
              <FormControl size="small" key={"moy"} sx={{ width: "100%" }}>
                <InputLabel>{t("monthOfYear")}</InputLabel>
                <ToggleMonths />
              </FormControl>
            </Box>
            <Box sx={{ mt: 1.5, display: isDatePickCompVisible }}>
              <FormControl size="small" key={"dom"} sx={{ width: "100%" }}>
                <InputLabel>{t("dateOfMonth")}</InputLabel>
                <ToggleDates />
              </FormControl>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <FormControl size="small" key={"periods"} sx={{ width: "100%" }}>
                <InputLabel>{t("period")}</InputLabel>
                <AppCard variant="outlined">
                  <CardContent sx={{ mt: 3, pb: "12px !important", display: "flex" }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={t("startDate")}
                        views={['year', 'month', 'day']}
                        format="yyyy-MM-dd"
                        readOnly={readOnly}
                        value={period.startDate}
                        onAccept={date => onChangePeriod(date, "startDate")}
                        slotProps={{
                          textField: { size: "small", sx: { ml: "2%", width: "42%", display: noEndDate ? "none" : "" } },
                          layout: { sx: PICKER_VIEW_SX },
                        }}
                      />
                      <NoEndDateStyledToggle
                        value="check"
                        selected={!noEndDate}
                        size="small"
                        disabled={readOnly}
                        sx={{ ml: "2%" }}
                        onChange={() => {
                          setNoEndDate(!noEndDate);
                        }}
                      >
                        <ArrowRightIcon />
                      </NoEndDateStyledToggle>
                      <DatePicker
                        label={t("endDate")}
                        views={['year', 'month', 'day']}
                        format="yyyy-MM-dd"
                        readOnly={readOnly}
                        value={period.endDate}
                        minDate={period.startDate}
                        onAccept={date => onChangePeriod(date, "endDate")}
                        slotProps={{
                          textField: { size: "small", sx: { ml: "2%", width: "42%", display: noEndDate ? "none" : "" } },
                          layout: { sx: PICKER_VIEW_SX },
                        }}
                      />
                    </LocalizationProvider>
                  </CardContent>
                </AppCard>
              </FormControl>
            </Box>
            {
              activeControl ? (
                <Box sx={{ mt: 1.5 }}>
                  <AppCard variant="outlined">
                    <CardContent sx={{ py: 0, paddingBottom: "0 !important", display: "flex" }}>
                      <FormControl sx={{ width: "100%" }}>
                        <FormControlLabel
                          control={<Checkbox name="activeFlg" checked={active} sx={{ ml: "auto" }} onChange={() => setActive(!active)} />}
                          label={t("activeFlg")}
                          labelPlacement="start"
                          sx={{ ml: 0.5, color: "rgba(0, 0, 0, 0.6)" }}
                        />
                      </FormControl>
                    </CardContent>
                  </AppCard>
                </Box>
              ) : ""
            }
          </>
        )
      }
    </BaseConfigModal>
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
    />
    <Snackbar
      open={snackGridInfo.open}
      onClose={() => setGridSnackInfo({ ...snackGridInfo, open: false })}
      severity={snackGridInfo.severity}
      content={snackGridInfo.content}
    />
    </>
  );
};

export default withTranslation()(CalendarModal);